import {isUndefined} from 'lodash';
import {ReactNode} from 'react';

import {newPalette, NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsNumericDataPoint,
  isNumericDataPoint,
} from '../../../../../models/api/analyticsMetricsModel';
import {getMetricRenderer, MetricNumericalRenderer} from '../../../../helpers/analyticsFormatHelpers';
import {getMetricLabels} from '../../../../helpers/metricLabels';
import {ChartSerie} from '../../../charts/commons/types';
import {AnyGraphMetric, AnyGraphMetricsEnum} from '../../helperTypes';
import {MockedGraphMetricData} from '../useGraphMetricData';

/*
 * Common Functions.
 */

export function createMockData<T>(series: Array<ChartSerie<T>>) {
  return [5, 7, 6, 8, 7, 4, 5].map((value, index) =>
    wrapObjectWithDefault(
      {
        type: AnalyticsDataPointTypesEnum.NUMERICAL,
        label: index.toString(),
        // Support for multi-graphs
        values: wrapObjectWithDefault<AnalyticsNumericDataPoint>({}, () => ({
          type: AnalyticsDataPointTypesEnum.NUMERICAL,
          value,
          previousValue: value,
        })),
      },
      () => value,
    ),
  ) as unknown as ReadonlyArray<T>;
}

/**
 * Helper function for getting the metric render for use when formatting the
 * y-axis on charts. Only returns the renderer if it's numerical, otherwise
 * returns undefined
 */
export function getNumericalSelectedMetricRenderer<T>(
  graphMetricData: AnyGraphMetric | MockedGraphMetricData<T> | undefined,
): MetricNumericalRenderer | undefined {
  // The graphMetric data doesn't contain the data type at the top level,
  // so we get the data type from the first entry in the values
  const firstEntry = graphMetricData?.values[0];

  const renderSelectedMetric =
    !firstEntry || !isNumericDataPoint(firstEntry) ? undefined : getMetricRenderer(firstEntry.type);

  return renderSelectedMetric;
}

/**
 * Checks that the `previousValues` exist in the metric data, and adds
 * the previous data to the series if they do
 */
export function maybeAddPreviousValue<T>(
  metricData: AnyGraphMetric | {values: ReadonlyArray<T>} | undefined,
  shouldDisplayPrevious: boolean,
  series: Array<ChartSerie<T>>,
) {
  if (!shouldDisplayPrevious) {
    return series;
  }

  // Verify that `previousValues` exist before including series
  if (
    Array.isArray(metricData?.values) &&
    metricData?.values.every((value: any) => !isUndefined(value.value) && !isUndefined(value.previousValue))
  ) {
    return [
      ...series,
      {
        accessor: (d: T) => (d as unknown as AnalyticsNumericDataPoint).previousValue,
        label: 'Previous',
        isPrevious: true,
        color: newPalette[NewPaletteColorsEnum.GREY].regular,
      },
    ];
  }

  return series;
}

/**
 * Sets the serie label for display in the tooltips (and anywhere else we want the labels).
 */
export function computeSeriesWithOptionalLabels<T>(
  metric: AnyGraphMetricsEnum | undefined,
  series: Array<ChartSerie<T>>,
  selectedMetricLabel: ReactNode,
  isMultipleMetrics: boolean,
) {
  if (!metric) {
    return series;
  }

  const chartSeries = seriesWithLabel(metric, series);

  // In case of a multiple metrics chart, we need to reset the serie label on each metric change.
  if (isMultipleMetrics) {
    chartSeries[0].label = selectedMetricLabel;
  }
  return chartSeries;
}

/*
 * Helpers.
 */

// This will return the target object. Every time you try to access an unknown property,
// `generateValueIfNotFound` will be called to populate that property first.
function wrapObjectWithDefault<U, T extends object = object>(
  target: T,
  generateValueIfNotFound: (property: string | number | symbol) => U,
): T & {[key: string]: U} {
  return new Proxy(target, {
    get(proxyTarget: object, prop: string) {
      if (!(prop in proxyTarget)) {
        // eslint-disable-next-line no-param-reassign
        (proxyTarget as any)[prop] = generateValueIfNotFound(prop);
      }

      return (proxyTarget as any)[prop];
    },
  }) as T & {[key: string]: U};
}

function seriesWithLabel<T>(metric: AnyGraphMetricsEnum, series: Array<ChartSerie<T>>) {
  const labels = getMetricLabels<AnyGraphMetricsEnum>(metric);

  return series.map((serie, index) => ({
    ...serie,
    // Fallback on server value if not found
    label: labels?.series?.[index]?.label ?? serie.label,
  }));
}
